/**
 * Import dialog — the primary input path for a weekly batch.
 *
 * Two source modes share the same preview + column-map + commit pipeline:
 *   - paste mode: user pastes TSV/CSV into a textarea; POST /api/xlsx/classify-paste
 *   - xlsx mode:  user uploads .xlsx; POST /api/xlsx/parse
 *
 * After parse, the user sees:
 *   - a sheet picker (xlsx mode; paste has a single sheet)
 *   - a classified preview grid (panel header / column header / total / data / empty)
 *   - a column mapping UI (auto-detected with manual override)
 *   - a commit button that posts every data row to the batch
 *
 * The backend canonicalizes raw rows (alias resolution, case-insensitive)
 * on insert, so this dialog only needs to get column mappings right.
 */
import { useEffect, useMemo, useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { api, ApiError } from '../api';
import type { ParsedSheet, ParsedWorkbookResponse } from '../api';
import {
  Button,
  Card,
  ErrorBanner,
  Pill,
  Select,
  Spinner,
  TextArea,
} from '../ui/primitives';

export type SourceMode = 'paste' | 'xlsx';

export interface ImportDialogProps {
  batchId: number;
  onClose: () => void;
  onImported: () => void;
  /** Pre-seeded from OCR: skips source selection and opens directly in preview. */
  preSeeded?: ParsedWorkbookResponse;
  /**
   * When set, only data rows whose rawAccountName is in this set are committed.
   * Used after the OcrAliasStep to restrict imports to mapped accounts.
   */
  allowedAccountNames?: Set<string>;
}

export function ImportDialog({
  batchId,
  onClose,
  onImported,
  preSeeded,
  allowedAccountNames,
}: ImportDialogProps) {
  const [mode, setMode] = useState<SourceMode>('paste');
  const [parsed, setParsed] = useState<ParsedWorkbookResponse | null>(preSeeded ?? null);
  const [activeSheet, setActiveSheet] = useState<number>(0);

  return (
    <div className="fixed inset-0 z-40 flex items-end justify-center bg-black/70 sm:items-center sm:p-4">
      <div className="flex h-[100dvh] w-full flex-col overflow-hidden border-t border-border bg-surface shadow-card sm:h-auto sm:max-h-[90dvh] sm:max-w-4xl sm:rounded-xl sm:border">
        <header className="flex items-center justify-between border-b border-border bg-surface-raised px-4 py-3">
          <div className="flex items-center gap-2">
            <h2 className="text-base font-semibold">Import upline rows</h2>
            <Pill tone={parsed ? 'green' : 'amber'}>
              {parsed ? 'parsed' : 'source not selected'}
            </Pill>
          </div>
          <Button variant="ghost" onClick={onClose}>
            Close
          </Button>
        </header>

        <div className="flex flex-1 flex-col gap-4 overflow-y-auto p-4">
          {!parsed && (
            <div className="flex gap-2">
              <button
                className={[
                  'flex-1 rounded-md border px-4 py-6 text-left',
                  mode === 'paste'
                    ? 'border-brand/30 bg-brand-light'
                    : 'border-border hover:bg-surface-raised',
                ].join(' ')}
                onClick={() => setMode('paste')}
              >
                <div className="text-sm font-medium">Paste TSV / CSV</div>
                <p className="mt-1 text-caption text-ink-muted">
                  Select rows in Excel, copy, paste here.
                </p>
              </button>
              <button
                className={[
                  'flex-1 rounded-md border px-4 py-6 text-left',
                  mode === 'xlsx'
                    ? 'border-brand/30 bg-brand-light'
                    : 'border-border hover:bg-surface-raised',
                ].join(' ')}
                onClick={() => setMode('xlsx')}
              >
                <div className="text-sm font-medium">Upload .xlsx</div>
                <p className="mt-1 text-caption text-ink-muted">
                  Full fidelity. Pick the sheet and row range next.
                </p>
              </button>
            </div>
          )}

          {!parsed && mode === 'paste' && (
            <PasteSource onParsed={setParsed} />
          )}
          {!parsed && mode === 'xlsx' && (
            <XlsxSource onParsed={setParsed} />
          )}

          {parsed && (
            <SheetAndCommit
              batchId={batchId}
              parsed={parsed}
              activeSheet={activeSheet}
              onSheetChange={setActiveSheet}
              onResetSource={preSeeded ? undefined : () => setParsed(null)}
              onImported={onImported}
              defaultNegateSign={!!preSeeded}
              allowedAccountNames={allowedAccountNames}
            />
          )}
        </div>
      </div>
    </div>
  );
}

function PasteSource({
  onParsed,
}: {
  onParsed: (p: ParsedWorkbookResponse) => void;
}) {
  const [text, setText] = useState('');
  const [err, setErr] = useState<string | null>(null);
  const mut = useMutation({
    mutationFn: () =>
      api.post<ParsedWorkbookResponse>('/api/xlsx/classify-paste', { text }),
    onSuccess: (p) => {
      setErr(null);
      onParsed(p);
    },
    onError: (e) => setErr(e instanceof ApiError ? e.message : String(e)),
  });
  return (
    <Card>
      <TextArea
        label="Paste rows"
        rows={10}
        value={text}
        onChange={(e) => setText(e.target.value)}
        placeholder={`Paste rows straight from Excel, e.g.
SAURABJI-CLICK
ACCOUNT\tCURRENCY\tCREDIT LIMIT\tPOINTS P&L\tRATE\tAMOUNT
CLICK ADMIN (BABA595)\tINR\t2.2 CR\t-327220.45\t0.06\t-19633.227
…`}
      />
      {err && (
        <div className="mt-2">
          <ErrorBanner>{err}</ErrorBanner>
        </div>
      )}
      <div className="mt-2">
        <Button disabled={mut.isPending || text.trim().length === 0} onClick={() => mut.mutate()}>
          {mut.isPending ? <Spinner /> : 'Parse'}
        </Button>
      </div>
    </Card>
  );
}

function XlsxSource({
  onParsed,
}: {
  onParsed: (p: ParsedWorkbookResponse) => void;
}) {
  const [file, setFile] = useState<File | null>(null);
  const [err, setErr] = useState<string | null>(null);
  const mut = useMutation({
    mutationFn: async () => {
      if (!file) throw new Error('Pick a file.');
      const base64 = await fileToBase64(file);
      return api.post<ParsedWorkbookResponse>('/api/xlsx/parse', {
        base64,
        originalFilename: file.name,
      });
    },
    onSuccess: (p) => {
      setErr(null);
      onParsed(p);
    },
    onError: (e) => setErr(e instanceof ApiError ? e.message : String(e)),
  });
  return (
    <Card>
      <label className="block text-caption font-medium text-ink">.xlsx file</label>
      <input
        type="file"
        accept=".xlsx"
        className="mt-1 block w-full text-caption text-ink-muted file:mr-3 file:cursor-pointer file:rounded-md file:border file:border-border file:bg-surface-raised file:px-3 file:py-1 file:text-small file:text-ink"
        onChange={(e) => setFile(e.target.files?.[0] ?? null)}
      />
      {err && (
        <div className="mt-2">
          <ErrorBanner>{err}</ErrorBanner>
        </div>
      )}
      <div className="mt-2">
        <Button disabled={mut.isPending || !file} onClick={() => mut.mutate()}>
          {mut.isPending ? <Spinner /> : 'Parse'}
        </Button>
      </div>
    </Card>
  );
}

// ---- Sheet picker + preview + column map + commit ----

type ColumnRole =
  | 'rawAccountName'
  | 'currencyLabelStated'
  | 'creditLimitStated'
  | 'pointsStated'
  | 'rateStated'
  | 'amountStated'
  | 'flags'
  | 'ignore';

const COLUMN_ROLES: { value: ColumnRole; label: string; required?: boolean }[] = [
  { value: 'ignore', label: '— ignore —' },
  { value: 'rawAccountName', label: 'Account (raw)', required: true },
  { value: 'currencyLabelStated', label: 'Currency' },
  { value: 'creditLimitStated', label: 'Credit limit' },
  { value: 'pointsStated', label: 'Points', required: true },
  { value: 'rateStated', label: 'Rate', required: true },
  { value: 'amountStated', label: 'Amount', required: true },
  { value: 'flags', label: 'Flags' },
];

function SheetAndCommit({
  batchId,
  parsed,
  activeSheet,
  onSheetChange,
  onResetSource,
  onImported,
  defaultNegateSign,
  allowedAccountNames,
}: {
  batchId: number;
  parsed: ParsedWorkbookResponse;
  activeSheet: number;
  onSheetChange: (i: number) => void;
  /** Omit to hide the ← Re-select button (used when pre-seeded from OCR). */
  onResetSource?: () => void;
  onImported: () => void;
  /** Default true when coming from OCR (upline images are opposite-signed). */
  defaultNegateSign?: boolean;
  /** When set, only rows with rawAccountName in this set are committed. */
  allowedAccountNames?: Set<string>;
}) {
  const qc = useQueryClient();
  const sheet: ParsedSheet | undefined = parsed.sheets[activeSheet];
  const [mapping, setMapping] = useState<Record<number, ColumnRole>>({});
  const [rowRange, setRowRange] = useState<{ from: number; to: number } | null>(null);
  const [commitErr, setCommitErr] = useState<string | null>(null);
  const [committedCount, setCommittedCount] = useState(0);
  const [negateSign, setNegateSign] = useState(defaultNegateSign ?? false);

  // Auto-detect mapping on sheet change.
  useEffect(() => {
    if (!sheet) return;
    const hdr = sheet.classifications.find((c) => c.kind === 'column_header');
    const headerRow = hdr ? sheet.rows[hdr.rowIndex] : null;
    const next: Record<number, ColumnRole> = {};
    if (headerRow) {
      for (let i = 0; i < headerRow.length; i++) {
        next[i] = guessRole(headerRow[i]);
      }
    }
    setMapping(next);
    // Default range = all data rows
    const dataIndexes = sheet.classifications
      .filter((c) => c.kind === 'data')
      .map((c) => c.rowIndex);
    if (dataIndexes.length > 0) {
      setRowRange({
        from: dataIndexes[0]!,
        to: dataIndexes[dataIndexes.length - 1]!,
      });
    } else {
      setRowRange(null);
    }
    setCommittedCount(0);
    setCommitErr(null);
  }, [sheet]);

  const missingRequired = useMemo(() => {
    const required: ColumnRole[] = [
      'rawAccountName',
      'pointsStated',
      'rateStated',
      'amountStated',
    ];
    const assigned = new Set(Object.values(mapping));
    return required.filter((r) => !assigned.has(r));
  }, [mapping]);

  const commitMut = useMutation({
    mutationFn: async () => {
      if (!sheet) throw new Error('No sheet selected.');
      if (!rowRange) throw new Error('No row range.');
      if (missingRequired.length > 0) {
        throw new Error(`Missing required columns: ${missingRequired.join(', ')}`);
      }
      // Panel assignment per row comes from the classifications we already
      // have. For each data row in the range, look up its panel from
      // sheet.classifications.
      const classByRow = new Map(
        sheet.classifications.map((c) => [c.rowIndex, c] as const),
      );
      let count = 0;
      for (let r = rowRange.from; r <= rowRange.to; r++) {
        const cls = classByRow.get(r);
        if (!cls || cls.kind !== 'data') continue;
        const row = sheet.rows[r];
        if (!row) continue;
        const picked = pickColumns(row, mapping);
        if (!picked) continue;
        if (allowedAccountNames && !allowedAccountNames.has(picked.rawAccountName)) continue;
        await api.post('/api/batches/' + batchId + '/raw-rows', {
          panel: cls.panel,
          rawAccountName: picked.rawAccountName,
          currencyLabelStated: picked.currencyLabelStated,
          creditLimitStated: picked.creditLimitStated,
          pointsStated: negateSign ? negateDecimal(picked.pointsStated) : picked.pointsStated,
          rateStated: picked.rateStated,
          amountStated: negateSign ? negateDecimal(picked.amountStated) : picked.amountStated,
          flags: picked.flags,
          sourceRowIndex: sheet.rowOffset + r,
        });
        count += 1;
        setCommittedCount(count);
      }
      return count;
    },
    onSuccess: () => {
      setCommitErr(null);
      void qc.invalidateQueries();
      onImported();
    },
    onError: (err) =>
      setCommitErr(err instanceof ApiError ? err.message : String(err)),
  });

  if (!sheet) return null;

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center gap-2">
        {onResetSource && (
          <Button variant="ghost" onClick={onResetSource}>
            ← Re-select source
          </Button>
        )}
        {parsed.sheets.length > 1 && (
          <Select
            label="Sheet"
            value={String(activeSheet)}
            onChange={(v) => onSheetChange(Number(v))}
            options={parsed.sheets.map((s, i) => ({
              value: String(i),
              label: `${s.name} (${s.rowCount} rows)`,
            }))}
          />
        )}
        <span className="text-caption text-ink-muted">
          {sheet.panels.length} panel(s), {sheet.classifications.filter((c) => c.kind === 'data').length} data row(s)
        </span>
      </div>

      <Card>
        <div className="mb-3 flex flex-wrap items-start justify-between gap-3">
          <div>
            <h3 className="text-small font-medium text-ink">Column mapping</h3>
            <p className="mt-1 text-[11px] text-ink-muted">
              Auto-detected from the column-header row. Rate must be a decimal (0.06, not 6%).
            </p>
          </div>
          <label className="flex cursor-pointer items-center gap-2 rounded-md border border-amber-200 bg-amber-50 px-3 py-2 text-caption text-amber-700">
            <input
              type="checkbox"
              checked={negateSign}
              onChange={(e) => setNegateSign(e.target.checked)}
              className="h-3.5 w-3.5 accent-amber-500"
            />
            <span>
              <span className="font-semibold">Negate signs</span>
              <span className="ml-1 text-amber-500">(flip ± on Points & Amount)</span>
            </span>
          </label>
        </div>
        <div className="overflow-x-auto">
          <table className="min-w-full text-xs">
            <thead className="text-left text-ink-muted">
              <tr>
                <th className="px-2 py-1">Col</th>
                <th className="px-2 py-1">Sample</th>
                <th className="px-2 py-1">Role</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-border">
              {(sheet.rows[0] ?? []).map((_, colIdx) => {
                const sample = sampleColumn(sheet, colIdx);
                const role = mapping[colIdx] ?? 'ignore';
                return (
                  <tr key={colIdx}>
                    <td className="px-2 py-1 font-semibold text-ink">
                      {letterFromIndex(colIdx)}
                    </td>
                    <td className="px-2 py-1 text-ink-muted">{sample}</td>
                    <td className="px-2 py-1">
                      <select
                        className="rounded-md border border-border bg-surface px-2 py-1 text-caption text-ink focus:border-brand focus:outline-none"
                        value={role}
                        onChange={(e) =>
                          setMapping({
                            ...mapping,
                            [colIdx]: e.target.value as ColumnRole,
                          })
                        }
                      >
                        {COLUMN_ROLES.map((r) => (
                          <option key={r.value} value={r.value}>
                            {r.label}
                          </option>
                        ))}
                      </select>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
        {missingRequired.length > 0 && (
          <p className="mt-3 text-caption text-warn">
            Missing required column: {missingRequired.join(', ')}
          </p>
        )}
      </Card>

      <Card>
        <h3 className="mb-2 text-small font-medium text-ink">Rows to import</h3>
        <p className="mb-3 text-caption text-ink-muted">
          Only rows classified as <Pill>data</Pill> within this range are sent.
          Headers, totals, and empties are skipped automatically.
        </p>
        {rowRange && (
          <div className="grid grid-cols-2 gap-3">
            <label className="text-caption text-ink-muted">
              From row (1-indexed)
              <input
                type="number"
                min={1}
                max={sheet.rowCount}
                value={rowRange.from + sheet.rowOffset}
                onChange={(e) =>
                  setRowRange({
                    ...rowRange,
                    from: Math.max(0, Number(e.target.value) - sheet.rowOffset),
                  })
                }
                className="mt-1 block w-full rounded-md border border-border bg-surface px-2 py-1 text-caption text-ink focus:border-brand focus:outline-none"
              />
            </label>
            <label className="text-caption text-ink-muted">
              To row (1-indexed)
              <input
                type="number"
                min={1}
                max={sheet.rowCount}
                value={rowRange.to + sheet.rowOffset}
                onChange={(e) =>
                  setRowRange({
                    ...rowRange,
                    to: Math.max(0, Number(e.target.value) - sheet.rowOffset),
                  })
                }
                className="mt-1 block w-full rounded-md border border-border bg-surface px-2 py-1 text-caption text-ink focus:border-brand focus:outline-none"
              />
            </label>
          </div>
        )}
        <PreviewTable sheet={sheet} rowRange={rowRange} mapping={mapping} />
      </Card>

      {commitErr && <ErrorBanner>{commitErr}</ErrorBanner>}

      <div className="flex items-center gap-2">
        <Button
          onClick={() => commitMut.mutate()}
          disabled={commitMut.isPending || missingRequired.length > 0 || !rowRange}
        >
          {commitMut.isPending ? (
            <span className="flex items-center gap-2">
              <Spinner /> committing {committedCount}…
            </span>
          ) : (
            'Commit rows'
          )}
        </Button>
        <span className="text-caption text-ink-muted">
          Raw rows are append-only. Edits require unlocking the batch.
        </span>
      </div>
    </div>
  );
}

function PreviewTable({
  sheet,
  rowRange,
  mapping,
}: {
  sheet: ParsedSheet;
  rowRange: { from: number; to: number } | null;
  mapping: Record<number, ColumnRole>;
}) {
  if (!rowRange) return null;
  const classByRow = new Map(sheet.classifications.map((c) => [c.rowIndex, c] as const));
  const slice = sheet.rows.slice(rowRange.from, rowRange.to + 1).map((row, i) => ({
    row,
    absIndex: rowRange.from + i,
  }));
  return (
    <div className="mt-3 overflow-x-auto">
      <table className="min-w-full font-mono text-[11px]">
        <thead className="text-left text-ink-muted">
          <tr>
            <th className="px-2 py-1">#</th>
            <th className="px-2 py-1">Class</th>
            <th className="px-2 py-1">Panel</th>
            {(sheet.rows[0] ?? []).map((_, i) => (
              <th key={i} className="px-2 py-1">
                {letterFromIndex(i)}
                {mapping[i] && mapping[i] !== 'ignore' && (
                  <span className="ml-1 rounded bg-surface-raised px-1 py-0.5 text-[10px] text-ink-muted ring-1 ring-border">
                    {mapping[i]}
                  </span>
                )}
              </th>
            ))}
          </tr>
        </thead>
        <tbody className="divide-y divide-border">
          {slice.map(({ row, absIndex }) => {
            const cls = classByRow.get(absIndex);
            const tone =
              cls?.kind === 'data'
                ? 'text-ink'
                : cls?.kind === 'panel_header'
                  ? 'text-brand'
                  : cls?.kind === 'total'
                    ? 'text-warn'
                    : 'text-ink-muted';
            return (
              <tr key={absIndex} className={tone}>
                <td className="px-2 py-1">{absIndex + sheet.rowOffset}</td>
                <td className="px-2 py-1">{cls?.kind ?? '—'}</td>
                <td className="px-2 py-1">
                  {cls?.kind === 'panel_header'
                    ? cls.panel
                    : cls?.kind === 'data'
                      ? (cls.panel ?? '—')
                      : ''}
                </td>
                {row.map((c, i) => (
                  <td key={i} className="px-2 py-1">
                    {c ?? ''}
                  </td>
                ))}
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

// ---- helpers ----

/** Strip thousand-separator commas: "1,242,962.33" → "1242962.33" */
function stripCommas(s: string): string {
  return s.replace(/,(?=\d)/g, '');
}

function pickColumns(
  row: (string | null)[],
  mapping: Record<number, ColumnRole>,
): {
  rawAccountName: string;
  currencyLabelStated?: string | null;
  creditLimitStated?: string | null;
  pointsStated: string;
  rateStated: string;
  amountStated: string;
  flags?: string[];
} | null {
  const byRole: Partial<Record<ColumnRole, string>> = {};
  for (const [idxStr, role] of Object.entries(mapping)) {
    if (role === 'ignore') continue;
    const v = row[Number(idxStr)];
    if (v === null || v === undefined) continue;
    byRole[role] = String(v).trim();
  }
  if (
    !byRole.rawAccountName ||
    !byRole.pointsStated ||
    !byRole.rateStated ||
    byRole.amountStated === undefined
  ) {
    return null;
  }
  return {
    rawAccountName: byRole.rawAccountName,
    currencyLabelStated: byRole.currencyLabelStated ?? null,
    creditLimitStated: byRole.creditLimitStated ?? null,
    pointsStated: stripCommas(byRole.pointsStated),
    rateStated: stripCommas(byRole.rateStated),
    amountStated: stripCommas(byRole.amountStated),
    flags: byRole.flags
      ? byRole.flags.split(/[,;\s]+/).filter((s) => s.length > 0)
      : [],
  };
}

function guessRole(v: string | null | undefined): ColumnRole {
  const up = (v ?? '').trim().toUpperCase();
  if (up === 'ACCOUNT') return 'rawAccountName';
  if (up === 'CURRENCY') return 'currencyLabelStated';
  if (up === 'CREDIT LIMIT') return 'creditLimitStated';
  if (up === 'POINTS P&L' || up === 'POINTS') return 'pointsStated';
  if (up === 'RATE') return 'rateStated';
  if (up === 'AMOUNT') return 'amountStated';
  if (up === 'FLAGS' || up === 'STATUS' || up === 'REMARKS') return 'flags';
  return 'ignore';
}

function letterFromIndex(i: number): string {
  let n = i;
  let s = '';
  while (n >= 0) {
    s = String.fromCharCode(65 + (n % 26)) + s;
    n = Math.floor(n / 26) - 1;
  }
  return s;
}

function sampleColumn(sheet: ParsedSheet, col: number): string {
  for (const c of sheet.classifications) {
    if (c.kind === 'data') {
      const v = sheet.rows[c.rowIndex]?.[col];
      if (v) return String(v);
    }
  }
  return '—';
}

/** Flip the sign of a decimal string: "-1234.5" → "1234.5", "1234.5" → "-1234.5". */
function negateDecimal(s: string): string {
  const t = s.trim();
  return t.startsWith('-') ? t.slice(1) : `-${t}`;
}

function fileToBase64(f: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => {
      const result = reader.result;
      if (typeof result !== 'string') {
        reject(new Error('unexpected FileReader result'));
        return;
      }
      const idx = result.indexOf(',');
      resolve(idx >= 0 ? result.slice(idx + 1) : result);
    };
    reader.onerror = () => reject(reader.error ?? new Error('FileReader failed'));
    reader.readAsDataURL(f);
  });
}
